import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openLink } from '@tma.js/sdk-react';
import { Eye } from 'lucide-react';

import { env } from '@/env';
import { useListOpenTournamentsQuery, useListMyTournamentsQuery } from '@/services/qduelsApi';
import { useHaptics } from '@/hooks/useHaptics';
import { crWatchRedirectUrl } from '@/lib/crWatchLink';
import { SectionCard } from '@/components/hub/SectionCard';
import { EmptyState } from '@/components/common/EmptyState';
import { PanelNotice, PanelWaiting } from '@/components/hub/PanelNotice';
import { listVerdict } from '@/lib/listState';
import { useStalled, useEverFailed, NO_ANSWER_MS } from '@/hooks/useStalled';
import { LiveTournamentSlots, type LiveMatch } from './live/LiveTournamentSlots';
import type { QduelsParty, QduelsTournamentSummary } from '@/types/qduels';

/**
 * Live — every tournament match with a valid spectator deep link, across global
 * / community / sit&go. The 1v1 matchmaking queue is NOT here (it isn't a
 * tournament).
 *
 * There is no server-side "live matches" endpoint, so this is a client fan-out:
 * discover in-progress tournaments (public via scope=open, community via the
 * caller-scoped list), then one `<LiveTournamentSlots>` per tournament fetches
 * that tournament's live slots and reports them up; we flatten into one list.
 * Poll cadence 15s (discovery is under the 60/min list limiter).
 */
const MAX_LIVE_TOURNAMENTS = 15;

export function LiveMatchesPanel() {
  const { t } = useTranslation('hub');
  const qduelsOn = env.featureQduelsTournamentsEnabled;

  const openQ = useListOpenTournamentsQuery(
    { status: 'in_progress' },
    { pollingInterval: 15000, refetchOnFocus: true, refetchOnMountOrArgChange: true, skip: !qduelsOn },
  );
  const mineQ = useListMyTournamentsQuery(
    { status: 'in_progress' },
    { pollingInterval: 15000, refetchOnFocus: true, refetchOnMountOrArgChange: true, skip: !qduelsOn },
  );

  // Public (global + sit&go) + the caller's own community, deduped by id.
  const discovered = useMemo<QduelsTournamentSummary[]>(() => {
    const open = openQ.data?.tournaments ?? [];
    const community = (mineQ.data?.tournaments ?? []).filter(
      (t) => t.visibility === 'community' && t.status === 'in_progress',
    );
    const seen = new Set<string>();
    const out: QduelsTournamentSummary[] = [];
    for (const tour of [...open, ...community]) {
      if (seen.has(tour.tournamentId)) continue;
      seen.add(tour.tournamentId);
      out.push(tour);
    }
    return out;
  }, [openQ.data, mineQ.data]);

  const capped = useMemo(() => discovered.slice(0, MAX_LIVE_TOURNAMENTS), [discovered]);
  const overflow = discovered.length - capped.length;

  const [liveById, setLiveById] = useState<Record<string, LiveMatch[]>>({});
  const onLive = useCallback((id: string, matches: LiveMatch[]) => {
    setLiveById((prev) => ({ ...prev, [id]: matches }));
  }, []);

  // Flatten only the currently-discovered ids, so a tournament that just left
  // in_progress drops out even if its stale entry lingers in state.
  const matches = useMemo(
    () => capped.flatMap((tour) => liveById[tour.tournamentId] ?? []),
    [capped, liveById],
  );

  // 🔴 QUESTA SEZIONE AFFERMAVA UN VUOTO SU UNA DOMANDA MAI POSTA.
  // Su `stg-tma` (06/09) `VITE_FEATURE_QDUELS_TOURNAMENTS_ENABLED=false` mette entrambe le
  // query in `skip`: non caricano, non falliscono, non rispondono — **non succede niente**.
  // `loading` era falso e `matches` vuoto, quindi lo schermo diceva «Nothing to watch right
  // now», che e' un fatto, e il fatto era falso: nessuno aveva guardato.
  //
  // `reachable: qduelsOn` e' esattamente la distinzione che mancava — «spenta da
  // configurazione» non e' «non c'e' niente». Precedenze in `lib/listState.ts`.
  const openAnswered = openQ.data !== undefined;
  const mineAnswered = mineQ.data !== undefined;
  const openFailed = useEverFailed(openQ.isError, openAnswered);
  const mineFailed = useEverFailed(mineQ.isError, mineAnswered);
  const openPending = qduelsOn && !openAnswered && !openFailed;
  const minePending = qduelsOn && !mineAnswered && !mineFailed;
  const openStalled = useStalled(openPending);
  const mineStalled = useStalled(minePending);
  const openNoAnswer = useStalled(openPending, NO_ANSWER_MS);
  const mineNoAnswer = useStalled(minePending, NO_ANSWER_MS);
  const verdict = listVerdict(matches.length, [
    {
      reachable: qduelsOn,
      loading: openQ.isLoading,
      slow: openStalled,
      failed: openFailed || openNoAnswer,
      answered: openAnswered,
    },
    {
      reachable: qduelsOn,
      loading: mineQ.isLoading,
      slow: mineStalled,
      failed: mineFailed || mineNoAnswer,
      answered: mineAnswered,
    },
  ]);

  const haptics = useHaptics();
  const watch = (url: string) => {
    haptics.impact('light');
    openLink.ifAvailable(crWatchRedirectUrl(url));
  };

  // I raccoglitori di dati sono invisibili — una fetch per torneo in corso —
  // ma devono restare montati anche quando non c'è niente da guardare:
  // smontandoli con la card, la sezione smetterebbe di accorgersi che una
  // partita è iniziata.
  const collectors = capped.map((tour) => (
    <LiveTournamentSlots key={tour.tournamentId} tournament={tour} onLive={onLive} />
  ));

  if (verdict !== 'rows') {
    return (
      <div className="flex h-full flex-col">
        {collectors}
        {(verdict === 'loading' || verdict === 'slow') && (
          <PanelWaiting slow={verdict === 'slow'} />
        )}
        {verdict === 'unavailable' && <PanelNotice tone="unavailable" />}
        {verdict === 'error' && <PanelNotice tone="error" />}
        {verdict === 'empty' && (
          <EmptyState art="eyes">
            {t('live.empty', {
              defaultValue: 'Nothing to watch right now. Check back when a match is live.',
            })}
          </EmptyState>
        )}
      </div>
    );
  }

  return (
    // La card-finestra esiste solo quando c'è un elenco da contenere.
    <SectionCard flush className="flex min-h-0 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain p-3 [touch-action:pan-y]">
        {collectors}

        {matches.map((m) => (
          <MatchRow key={`${m.tournamentId}-${m.slot}`} match={m} onWatch={watch} />
        ))}

        {overflow > 0 && (
          <p className="pt-1 text-center text-[11px] text-fg-muted">
            {t('live.capped', {
              count: overflow,
              defaultValue: '+{{count}} more live tournaments not shown',
            })}
          </p>
        )}
      </div>
    </SectionCard>
  );
}

function partyName(p: QduelsParty | null): string {
  return p ? p.playerName || p.playerTag : 'TBD';
}

function MatchRow({ match, onWatch }: { match: LiveMatch; onWatch: (url: string) => void }) {
  const { t } = useTranslation('duels');
  return (
    <div className="card-edge rounded-xl bg-bg-secondary p-2.5">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-xs font-semibold text-fg">
          {partyName(match.a)} <span className="text-fg-muted">vs</span> {partyName(match.b)}
        </span>
        <span className="shrink-0 text-[10px] text-fg-muted">{match.tournamentName}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-fg-muted">
          <Eye aria-hidden className="h-3 w-3" />
          {t('tournaments.watch.label', { defaultValue: 'Watch' })}
        </span>
        {match.spectatorAurl && <WatchButton label={partyName(match.a)} onClick={() => onWatch(match.spectatorAurl!)} />}
        {match.spectatorBurl && <WatchButton label={partyName(match.b)} onClick={() => onWatch(match.spectatorBurl!)} />}
      </div>
    </div>
  );
}

function WatchButton({ label, onClick }: { label: string; onClick: () => void }) {
  const { t } = useTranslation('duels');
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('tournaments.watch.pov', { defaultValue: 'Watch {{name}}', name: label })}
      className="inline-flex min-w-0 max-w-[9rem] items-center gap-1 rounded-md bg-bg-tertiary px-2 py-1 text-[11px] font-semibold text-brand transition-opacity active:opacity-80"
    >
      <Eye aria-hidden className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}
